import { useDebounceFn } from '@vueuse/core'

export default function useSearch({
  showButton = true,
  onSearch,
  searchTerm,
}: {
  showButton?: boolean
  onSearch?: () => void
  searchTerm?: Ref<string>
} = {}) {
  const route = useRoute()
  const router = useRouter()
  const internalSearchTerm = searchTerm || ref('')
  const searchQuery = ref<Record<string, string | number>>({})

  function setSearchTerm() {
    const searchTermTrimmed = internalSearchTerm.value.trim()
    if (
      searchTermFromQuery.value !== searchTermTrimmed &&
      searchTermTrimmed.length >= 3
    ) {
      searchQuery.value.term = searchTermTrimmed
      // Reset page to 1 when search term changes
      searchQuery.value.page = 1
      router.push({
        path: '/search/',
        query: {
          ...route.query,
          ...searchQuery.value,
        },
      })
      onSearch?.()
    }
  }

  function updateSearchTerm() {
    const newSearchTerm = (route.query?.term as string) || ''
    if (newSearchTerm !== internalSearchTerm.value) {
      internalSearchTerm.value = newSearchTerm
      if (newSearchTerm) {
        onSearch?.()
      }
    }
  }

  const debouncedSearch = useDebounceFn(setSearchTerm, 1100)

  const searchTermFromQuery = computed(() => route.query?.term || '')

  watch(internalSearchTerm, () => {
    if (!showButton) debouncedSearch()
  })

  // Watch all query parameters except page
  watch(
    () => {
      const { page, ...otherQuery } = route.query
      return otherQuery
    },
    (newQuery, oldQuery) => {
      // If any non-page parameter changed, reset to page 1
      if (
        route.path === '/search/' &&
        JSON.stringify(newQuery) !== JSON.stringify(oldQuery)
      ) {
        router.push({
          path: '/search/',
          query: {
            ...route.query,
            page: 1,
          },
        })
      }
      updateSearchTerm()
    },
    { immediate: true, deep: true },
  )

  watch(
    () => route.path,
    () => {
      updateSearchTerm()
    },
  )

  return {
    searchTerm: internalSearchTerm,
    setSearchTerm,
  }
}
